import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import AlertComponent from "../../components/AlertComponent";
import { Context } from "../../Reducers/Store";

const SENHA_REQUISITOS = [
  "ter pelo menos 8 caracteres",
  "ter uma letra maiúscula",
  "ter uma letra minúscula",
  "ter um número",
];

function validarSenha(senha) {
  if (senha.length < 8) return false;
  if (!/[A-Z]/.test(senha)) return false;
  if (!/[a-z]/.test(senha)) return false;
  return /\d/.test(senha);
}

function extrairMensagemErro(err) {
  const data = err.response?.data;
  const mensagem = data?.mensagem || data?.message;
  const erros = Array.isArray(data?.erros) ? data.erros.filter(Boolean) : [];

  if (erros.length > 0) {
    return [mensagem, ...erros].filter(Boolean).join("\n");
  }

  if (data?.errors && typeof data.errors === "object") {
    const detalhes = Object.values(data.errors).flat().filter(Boolean);
    if (detalhes.length > 0) {
      return detalhes.join("\n");
    }
  }

  return mensagem || "Não foi possível aceitar o convite";
}

export default function AceitarConviteAdmin() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [, dispatch] = useContext(Context);
  const [convite, setConvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [form, setForm] = useState({
    primeiroNome: "",
    ultimoNome: "",
    senha: "",
  });

  useEffect(() => {
    let cancelado = false;
    axios
      .get(`${process.env.REACT_APP_API}/convite-administracao/${token}`)
      .then((res) => {
        if (!cancelado) setConvite(res.data);
      })
      .catch((err) => {
        console.error(err);
        AlertComponent("Convite inválido", "Não foi possível carregar este convite", { icon: "error" });
      })
      .finally(() => {
        if (!cancelado) setLoading(false);
      });

    return () => {
      cancelado = true;
    };
  }, [token]);

  async function aceitar(e) {
    e.preventDefault();
    if (!convite || convite.expirado || salvando) return;

    if (!convite.usuarioJaExiste && !validarSenha(form.senha)) {
      AlertComponent(
        "Senha inválida",
        `A senha precisa ${SENHA_REQUISITOS.join(", ")}.`,
        { icon: "warning" },
        null,
        false
      );
      return;
    }

    setSalvando(true);
    try {
      const payload = convite.usuarioJaExiste
        ? {}
        : {
            primeiroNome: form.primeiroNome,
            ultimoNome: form.ultimoNome,
            senha: form.senha,
          };

      const res = await axios.post(`${process.env.REACT_APP_API}/convite-administracao/${token}/aceitar`, payload);
      dispatch({ type: "SET_LOGGED", payload: { token: res.data.token } });
      AlertComponent("Convite aceito", "Você já pode acessar o painel", { icon: "success", timer: 1800 });
      navigate("/eventos");
    } catch (err) {
      console.error(err);
      AlertComponent("Erro", extrairMensagemErro(err), { icon: "error" }, null, false);
    } finally {
      setSalvando(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-violet-300 border-t-violet-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (!convite) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-white border border-slate-200 rounded-xl p-8 shadow-sm text-center">
          <h1 className="text-xl font-bold text-slate-800">Convite não encontrado</h1>
          <button onClick={() => navigate("/login")} className="mt-5 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold">
            Ir para login
          </button>
        </div>
      </div>
    );
  }

  const conviteDeEvento = !!convite.eventoNome;
  const papelLabel = conviteDeEvento
    ? convite.controleTotal ? "Controle Total" : "Controle Parcial"
    : convite.papel === 0
      ? "Administrador"
      : "Usuário";

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-lg bg-white border border-slate-200 rounded-xl p-8 shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-wider text-violet-600 mb-2">
          {conviteDeEvento ? "Evento compartilhado" : "Usuário do organizador"}
        </p>
        <h1 className="text-2xl font-bold text-slate-800">Aceitar convite</h1>
        <p className="text-sm text-slate-500 mt-2">
          {conviteDeEvento ? (
            <>
              {convite.organizadorNome} compartilhou a administração de <strong>{convite.eventoNome}</strong> com {convite.email}.
            </>
          ) : (
            <>
              {convite.organizadorNome} convidou {convite.email} para acessar o painel como usuário deste organizador.
            </>
          )}
        </p>

        <div className="mt-5 rounded-lg bg-slate-50 border border-slate-100 p-4 text-sm text-slate-600">
          <p><strong>Acesso:</strong> {papelLabel}</p>
          <p><strong>Cria layouts:</strong> {convite.podeCriarLayouts ? "Sim" : "Não"}</p>
          <p><strong>Escolhe WhatsApp:</strong> {convite.podeEscolherWhatsapp ? "Sim" : "Não"}</p>
        </div>

        {convite.expirado ? (
          <div className="mt-6 rounded-lg bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800">
            Este convite expirou. Peça para o organizador enviar um novo convite.
          </div>
        ) : convite.status === 1 ? (
          <div className="mt-6">
            <button onClick={() => navigate("/login")} className="w-full px-4 py-3 rounded-lg bg-slate-900 text-white text-sm font-semibold">
              Entrar no painel
            </button>
          </div>
        ) : (
          <form onSubmit={aceitar} className="mt-6 space-y-4">
            {!convite.usuarioJaExiste && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Nome</label>
                    <input
                      value={form.primeiroNome}
                      onChange={(e) => setForm((p) => ({ ...p, primeiroNome: e.target.value }))}
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Sobrenome</label>
                    <input
                      value={form.ultimoNome}
                      onChange={(e) => setForm((p) => ({ ...p, ultimoNome: e.target.value }))}
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Senha</label>
                  <input
                    type="password"
                    minLength={8}
                    pattern="(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9]).{8,}"
                    title="Use pelo menos 8 caracteres, uma letra maiúscula, uma letra minúscula e um número."
                    autoComplete="new-password"
                    value={form.senha}
                    onChange={(e) => setForm((p) => ({ ...p, senha: e.target.value }))}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400"
                    required
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    Use pelo menos 8 caracteres, uma maiúscula, uma minúscula e um número.
                  </p>
                </div>
              </>
            )}
            <button disabled={salvando} className="w-full px-4 py-3 rounded-lg bg-slate-900 text-white text-sm font-semibold disabled:opacity-60">
              {salvando ? "Aceitando..." : "Aceitar convite"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
